import React, { useState, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { remove } from 'local-storage'
import { useUserDispatcher } from '@hooks'
import TextField from '@material-ui/core/TextField'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Button from '@material-ui/core/Button'
import Select from '@material-ui/core/Select'
import InputLabel from '@material-ui/core/InputLabel'
import SaveIcon from '@material-ui/icons/Save'
import FormControl from '@material-ui/core/FormControl'
import { UpdateVideoMaterial, VIDEOTYPE } from '@services/app/mutation'
import appClient from '@services'
import { useMutation } from '@apollo/client'
import { IServiceResponse } from '@services/model'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 350,
    '& > *': {
      margin: theme.spacing(1),
    },
  },
}))


interface IEditVideoProps {
  open: boolean,
  data: any,
  handleClose: () => void
}

const EditVideo: React.FC<IEditVideoProps> = ({ open, data, handleClose }) => {
  const { clearUserAuthData } = useUserDispatcher()
  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(false)
  const [name, setName] = useState(data.name)
  const [errorName, setErrorName] = useState(false)
  const [errorSequence, setErrorSequence] = useState(false)
  const isError = useMemo(() => {
    return errorName || errorSequence
  }, [errorName, errorSequence])
  const [description, setDescription] = useState(data.description)
  const [sequence, setSequence] = useState(data.sequence | 0)
  const [videoType, setVideoType] = useState(data.videoType)
  const [updateVideoMaterial] = useMutation(UpdateVideoMaterial, { client: appClient })

  const goToLogin = () => {
    remove('token')
    clearUserAuthData()
  }

  const handleUpdateFields = async () => {
    if (isError) return

    try {
      setIsLoading(true)
      const updateVideoMaterialVariables = {
        id: data.id,
        name: name.trim(),
        sequence,
        videoType,
        description: description?.trim() ?? ''
      }

      const updateVideoMaterialResponse = await updateVideoMaterial({ variables: updateVideoMaterialVariables })

      const { result } = updateVideoMaterialResponse.data.updateVideoMaterial as IServiceResponse<boolean>
      if (result) {
        handleClose(true)
      }
    } catch (e) {
      if (e?.graphQLErrors[0]?.extensions?.code == "authorization") {
        goToLogin()
        return
      }
      console.error(e)
    }
    setIsLoading(false)
  }

  return (
    (<Dialog
      disableBackdropClick
      disableEscapeKeyDown
      open={open}
      onClose={() => handleClose()}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle>Edit: {name}</DialogTitle>
      <DialogContent>
        <form className={classes.root} noValidate autoComplete="off">
          <FormControl fullWidth>
            <TextField
              id="Name"
              label="Name*"
              value={name}
              fullWidth
              disabled={isLoading}
              onChange={e => {
                const value = e.currentTarget.value
                setName(value)
                setErrorName(value === '')
              }}
            />
            {errorName && <span style={{ color: 'red', fontSize: 12 }}>Name is required</span>}
          </FormControl>

          <FormControl fullWidth>
            <TextField
              id="Description"
              label="Description"
              disabled={isLoading}
              value={description}
              fullWidth
              onChange={e => setDescription(e.currentTarget.value)}
            />
          </FormControl>

          <FormControl fullWidth>
            <TextField
              type="number"
              id="Sequence"
              label="Sequence*"
              disabled={isLoading}
              value={sequence}
              fullWidth
              onChange={e => {
                const value = parseInt(e.currentTarget.value)
                setSequence(value)
                setErrorSequence(isNaN(value) || value < 0)
              }}
            />
            {errorSequence && <span style={{ color: 'red', fontSize: 12 }}>Sequence should be greater than 0</span>}
          </FormControl>

          <FormControl fullWidth>
            <InputLabel htmlFor="videoType">Video Type*</InputLabel>
            <Select
              native
              value={videoType}
              disabled={isLoading}
              onChange={e => setVideoType(e.currentTarget.value)}
              inputProps={{
                name: 'videoType',
                id: 'videoType',
              }}
              fullWidth
            >
              <option value={VIDEOTYPE.SUPPORTMATERIAL}>Support Material</option>
              <option value={VIDEOTYPE.MATERIAL}>Skill Material</option>
            </Select>
          </FormControl>
        </form>
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={() => handleClose()} color="primary">
          Cancel
        </Button>

        <Button disabled={isLoading} variant="contained" startIcon={<SaveIcon />} onClick={handleUpdateFields} color="primary" autoFocus>
          Update
        </Button>
      </DialogActions>
    </Dialog>)
  )
}

export default EditVideo